import { useMemo, useState } from 'react'
import { usePlayerList } from '@/features/squad/queries'
import { useMostPickedPlayer } from '@/features/league/queries'
import { suggestPicks } from '@/features/squad/lib/suggestPicks'
import type { LeagueSummary } from '@/features/league/types'
import type { PlayerListItem } from '@/features/squad/types'
import { competitionOptions } from '../lib/competitionOptions'
import { CompetitionSelect } from './CompetitionSelect'
import styles from './HighlightsCard.module.css'

type HighlightsCardProps = {
  leagues: LeagueSummary[]
}

function eligible(players: PlayerListItem[]): PlayerListItem[] {
  return players.filter(p => !p.isEliminated && p.isAvailable)
}

function topGwScorer(players: PlayerListItem[]): PlayerListItem | null {
  let best: PlayerListItem | null = null
  for (const p of players) {
    if (p.gwPoints === null) continue
    if (best === null || p.gwPoints > (best.gwPoints ?? -Infinity)) best = p
  }
  return best
}

function bestValue(players: PlayerListItem[], excludeId?: string): PlayerListItem | null {
  let best: PlayerListItem | null = null
  let bestRatio = 0
  for (const p of eligible(players)) {
    if (p.playerId === excludeId) continue
    if (p.price <= 0 || p.avgPoints === null) continue
    const ratio = p.avgPoints / p.price
    if (ratio > bestRatio) { bestRatio = ratio; best = p }
  }
  return best
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileLabel}>{label}</div>
      <div className={styles.tileValue}>{value}</div>
      <div className={styles.tileSub}>{sub}</div>
    </div>
  )
}

export function HighlightsCard({ leagues }: HighlightsCardProps) {
  const competitions = useMemo(() => competitionOptions(leagues), [leagues])
  const [competitionId, setCompetitionId] = useState(() => competitions[0]?.id ?? '')

  const inComp = leagues.filter(l => l.competitionId === competitionId)
  const target = inComp.find(l => l.isFavorite) ?? inComp[0] ?? leagues[0]
  const leagueId = target?.id ?? ''

  const { data, isPending, isError } = usePlayerList({
    search:        '',
    position:      'ALL',
    sortBy:        'avgPoints',
    sortDir:       'desc',
    competitionId,
    teamCode:      '',
    minPrice:      null,
    maxPrice:      null,
    hideEliminated: false,
  })

  const mostPicked = useMostPickedPlayer(leagueId, undefined, target?.windowState)

  const players  = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data])
  const scorer   = useMemo(() => topGwScorer(players), [players])
  const prospect = useMemo(() => suggestPicks(players, 1)[0]?.player ?? null, [players])
  const value    = useMemo(() => bestValue(players, prospect?.playerId), [players, prospect])

  if (!target) return null

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>Destaques</span>
        <CompetitionSelect
          competitions={competitions}
          value={competitionId}
          onChange={setCompetitionId}
        />
      </div>

      {isPending ? (
        <div className={styles.state}>Carregando destaques…</div>
      ) : isError ? (
        <div className={styles.state}>Não foi possível carregar os destaques.</div>
      ) : (
        <div className={styles.tiles}>
          <Tile
            label="Artilheiro da rodada"
            value={scorer ? scorer.displayName : '—'}
            sub={scorer && scorer.gwPoints !== null ? `${scorer.gwPoints} pts na última rodada` : 'Aguardando a primeira rodada'}
          />
          <Tile
            label="Promessa da próxima"
            value={prospect ? prospect.displayName : '—'}
            sub={prospect && prospect.avgPoints !== null ? `~${prospect.avgPoints.toFixed(1)} pts por rodada` : 'Sem projeção ainda'}
          />
          <Tile
            label="Melhor custo-benefício"
            value={value ? value.displayName : '—'}
            sub={value && value.avgPoints !== null ? `${value.avgPoints.toFixed(1)} pts · $${value.price.toFixed(1)}` : 'Sem dados ainda'}
          />
          <Tile
            label={`Mais escalado · ${target.name}`}
            value={mostPicked.data ? mostPicked.data.name : '—'}
            sub={mostPicked.data ? `${mostPicked.data.pickPercentage}% dos times` : 'Disponível com a janela fechada'}
          />
        </div>
      )}
    </div>
  )
}
